import { memo, useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BlockIcon from '@mui/icons-material/Block';
import { useTranslation } from 'react-i18next';
import { K, getAdapterTypeLabel, apiT } from '../apiServiceI18n';
import ApiServiceStatusChip from './ApiServiceStatusChip';

const iconSx = { color: '#5b6473' };

function CellText({ value, title }) {
  const { t } = useTranslation();
  const display = value || t(K.API_SVC_EM_DASH);
  return (
    <Tooltip title={title ?? display} placement="top-start">
      <Typography
        component="span"
        variant="body2"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          pr: 0.5,
        }}
      >
        {display}
      </Typography>
    </Tooltip>
  );
}

function ApiServiceTableRow({
  row,
  gridCols,
  showAccountCol,
  canModify,
  onStatusToggle,
  onDetails,
  onPerformance,
  onWorkflow,
  onView,
  onEdit,
  onDelete,
  onExecutionHistory,
}) {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const isExecuting = row.executingCount != null && row.executingCount > 0;

  const lastExecutedDisplay = isExecuting
    ? row.lastExecuted || apiT(K.API_SVC_EXECUTING, { 0: row.executingCount })
    : row.lastExecuted || t(K.API_SVC_NA);

  const lastExecutedTitle =
    isExecuting
      ? `${lastExecutedDisplay} — ${t(K.API_SVC_REMAINING_TOOLTIP)}`
      : lastExecutedDisplay;

  const openMenu = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const closeMenu = () => setMenuAnchor(null);

  const run = (handler) => () => {
    closeMenu();
    handler(row);
  };

  const isEnabled = row.status === 'enabled';

  return (
    <Box
      role="row"
      sx={{
        display: 'grid',
        gridTemplateColumns: gridCols,
        px: 1.5,
        py: 1.1,
        alignItems: 'center',
        borderTop: '1px solid #e1e5ec',
        bgcolor: 'white',
        '&:nth-of-type(even)': { bgcolor: '#f9fafc' },
        '&:hover': { bgcolor: '#f0f3f8' },
      }}
    >
      <CellText value={row.name} />
      <Box>
        <ApiServiceStatusChip status={row.status} />
      </Box>
      {showAccountCol && <CellText value={row.account} />}
      <CellText value={row.workflowGroup} />
      <CellText value={row.workflowName} />
      <CellText value={getAdapterTypeLabel(row.adapter, t)} />
      <CellText value={row.authorization} />
      <CellText value={row.createdDate} />
      <CellText value={lastExecutedDisplay} title={lastExecutedTitle} />
      <CellText value={row.version} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title={t(K.API_SVC_ROW_ACTIONS)}>
          <IconButton
            size="small"
            onClick={openMenu}
            aria-label={t(K.API_SVC_ROW_ACTIONS)}
            sx={{
              color: '#6a7382',
              border: '1px solid #d2d8e2',
              borderRadius: 1,
              '&:hover': { bgcolor: '#eef1f6', borderColor: '#b8c3d4' },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              elevation: 3,
              sx: { minWidth: 200, borderRadius: 1.5, mt: 0.5 },
            },
          }}
        >
          <MenuItem onClick={run(onDetails)} dense>
            <ListItemIcon>
              <InfoOutlinedIcon fontSize="small" sx={iconSx} />
            </ListItemIcon>
            <ListItemText>{t(K.API_SVC_MENU_DETAILS)}</ListItemText>
          </MenuItem>

          <MenuItem onClick={run(onPerformance)} dense>
            <ListItemIcon>
              <BarChartOutlinedIcon fontSize="small" sx={iconSx} />
            </ListItemIcon>
            <ListItemText>{t(K.API_SVC_MENU_PERFORMANCE)}</ListItemText>
          </MenuItem>

          <MenuItem onClick={run(onWorkflow)} dense>
            <ListItemIcon>
              <AccountTreeOutlinedIcon fontSize="small" sx={iconSx} />
            </ListItemIcon>
            <ListItemText>{t(K.API_SVC_MENU_WORKFLOW)}</ListItemText>
          </MenuItem>

          {canModify ? (
            <MenuItem onClick={run(onEdit)} dense>
              <ListItemIcon>
                <EditOutlinedIcon fontSize="small" sx={iconSx} />
              </ListItemIcon>
              <ListItemText>{t(K.API_SVC_MENU_EDIT)}</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem onClick={run(onView)} dense>
              <ListItemIcon>
                <VisibilityOutlinedIcon fontSize="small" sx={iconSx} />
              </ListItemIcon>
              <ListItemText>{t(K.API_SVC_MENU_VIEW)}</ListItemText>
            </MenuItem>
          )}

          <MenuItem onClick={run(onExecutionHistory)} dense>
            <ListItemIcon>
              <HistoryOutlinedIcon fontSize="small" sx={iconSx} />
            </ListItemIcon>
            <ListItemText>{t(K.API_SVC_MENU_EXEC_HISTORY)}</ListItemText>
          </MenuItem>

          {canModify && (
            <>
              <Divider />
              {isEnabled ? (
                <MenuItem onClick={run(onStatusToggle)} dense>
                  <ListItemIcon>
                    <BlockIcon fontSize="small" sx={iconSx} />
                  </ListItemIcon>
                  <ListItemText>{t(K.API_SVC_MENU_DISABLE)}</ListItemText>
                </MenuItem>
              ) : (
                <MenuItem onClick={run(onStatusToggle)} dense>
                  <ListItemIcon>
                    <PlayArrowIcon fontSize="small" sx={iconSx} />
                  </ListItemIcon>
                  <ListItemText>{t(K.API_SVC_MENU_ENABLE)}</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={run(onDelete)} dense sx={{ color: 'error.main' }}>
                <ListItemIcon>
                  <DeleteOutlineIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>{t(K.API_SVC_MENU_DELETE)}</ListItemText>
              </MenuItem>
            </>
          )}
        </Menu>
      </Box>
    </Box>
  );
}

export default memo(ApiServiceTableRow);
